import React from 'react';
import { Tag } from '../onyx/Tag';

const STRUCTURED_TEMPLATE_IDS = new Set([
  'prescreen.structured',
  'preferences.structured',
  'updates.structured',
]);

const MRR_TEMPLATE_PREFIX = 'mrr.';

/** Fallback when older notes lack payload.field_order (jsonb scramble). */
const STRUCTURED_FIELD_ORDER_FALLBACK = {
  preferences: [
    'preferences:shipping',
    'preferences:shipping_sameas_visit',
    'preferences:visit_address',
    'preferences:phone',
    'preferences:contact_method',
    'preferences:special_instructions',
  ],
  updates: [
    'updates:future_medical_changes',
    'updates:medical_changes_detail',
    'updates:vaccines_3months',
    'updates:vaccine_date_certainty',
    'updates:vaccine_exact_date',
    'updates:vaccine_approx_date',
  ],
};

function formatFieldValue(value) {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object' && !Array.isArray(value)) {
    const parts = [
      value.address_1,
      value.address_2,
      value.city,
      value.state,
      value.zip_code,
    ].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function orderedStructuredFieldEntries(payload = {}) {
  const fields = payload.fields && typeof payload.fields === 'object'
    ? payload.fields
    : {};
  const keys = Object.keys(fields);
  if (keys.length === 0) return [];

  const explicit = Array.isArray(payload.field_order)
    ? payload.field_order.filter((key) => Object.prototype.hasOwnProperty.call(fields, key))
    : [];
  const noteType = String(payload.note_type || '').toLowerCase();
  const fallback = STRUCTURED_FIELD_ORDER_FALLBACK[noteType]
    || (keys.some((k) => k.startsWith('updates:'))
      ? STRUCTURED_FIELD_ORDER_FALLBACK.updates
      : null)
    || (keys.some((k) => k.startsWith('preferences:'))
      ? STRUCTURED_FIELD_ORDER_FALLBACK.preferences
      : null)
    || [];

  const ordered = [];
  const seen = new Set();
  [...explicit, ...fallback].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(fields, key) && !seen.has(key)) {
      ordered.push(key);
      seen.add(key);
    }
  });
  keys.forEach((key) => {
    if (!seen.has(key)) ordered.push(key);
  });
  return ordered.map((key) => [key, fields[key]]);
}

function StructuredNoteBody({ note }) {
  const payload = note?.payload || {};
  const labels = payload.labels || {};
  const entries = orderedStructuredFieldEntries({
    ...payload,
    note_type: note?.note_type || payload.note_type,
  });
  if (entries.length === 0) {
    return <p className="onyx-note-body">{payload.text || note?.text || '—'}</p>;
  }
  return (
    <dl className="onyx-note-structured-list">
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{labels[key] || key}</dt>
          <dd>{formatFieldValue(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function MrrNoteBody({ note }) {
  const payload = note?.payload || {};
  const text = payload.text || note?.text || '';
  const tags = payload.tag_slugs || [];
  return (
    <div className="onyx-note-mrr-body">
      <p className="onyx-note-body">{text}</p>
      {Array.isArray(tags) && tags.length > 0 ? (
        <div className="onyx-note-tags">
          {tags.map((slug) => (
            <Tag key={slug} label={slug} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FreeformNoteBody({ note }) {
  const payload = note?.payload || {};
  const title = note?.title || payload.title || '';
  const body = payload.text || note?.text || payload.body || '';
  return (
    <div className="onyx-note-freeform">
      {title ? <p className="onyx-note-freeform__title">{title}</p> : null}
      <p className="onyx-note-body">{body}</p>
    </div>
  );
}

function TextNoteBody({ note }) {
  const text = note?.text || note?.payload?.text || '';
  return <p className="onyx-note-body">{text || '—'}</p>;
}

const TEMPLATE_RENDERERS = {
  'prescreen.structured': StructuredNoteBody,
  'preferences.structured': StructuredNoteBody,
  'updates.structured': StructuredNoteBody,
  'mrr.eligible': MrrNoteBody,
  'mrr.ineligible': MrrNoteBody,
  'comment.freeform': FreeformNoteBody,
  'study_visit.freeform': FreeformNoteBody,
  'event.freeform': FreeformNoteBody,
};

const NOTE_TYPE_RENDERERS = {
  mrr: MrrNoteBody,
  comment: FreeformNoteBody,
  study_visit: FreeformNoteBody,
  event: FreeformNoteBody,
  prescreen: StructuredNoteBody,
  preferences: StructuredNoteBody,
  updates: StructuredNoteBody,
};

export function resolveNoteRenderer(note) {
  const templateId = note?.template_id || '';
  if (TEMPLATE_RENDERERS[templateId]) {
    return TEMPLATE_RENDERERS[templateId];
  }
  if (STRUCTURED_TEMPLATE_IDS.has(templateId)) {
    return StructuredNoteBody;
  }
  if (templateId.startsWith(MRR_TEMPLATE_PREFIX)) {
    return MrrNoteBody;
  }
  const noteType = (note?.note_type || '').toLowerCase();
  if (NOTE_TYPE_RENDERERS[noteType]) {
    return NOTE_TYPE_RENDERERS[noteType];
  }
  return TextNoteBody;
}

export function renderNoteBody(note) {
  const Component = resolveNoteRenderer(note);
  return <Component note={note} />;
}
